import { useMemo } from 'react'

import { useGetMetaQuery } from '../../../api/ua-insights'
import type { GenreTaxonomy, ObjectId, PatternTerm, TermRef } from '../types'

/** A story pattern or sub-pattern resolved from `/meta`, with its parent when it is a sub-pattern. */
export interface ResolvedPattern {
  pattern: TermRef
  parent: PatternTerm | null
}

export interface UAInsightsMeta {
  /** Display order; the first is the default. */
  networks: TermRef[]
  months: readonly string[]
  genres: GenreTaxonomy[]
  /** Canonical display order. */
  motivations: TermRef[]
  dimensions: TermRef[]
  patterns: PatternTerm[]
  defaultNetworkId: ObjectId | null
  defaultMonth: string | null
  networkById: (id: ObjectId) => TermRef | undefined
  genreById: (id: ObjectId) => GenreTaxonomy | undefined
  motivationById: (id: ObjectId) => TermRef | undefined
  /** For the maps keyed by motivation code (`motivationProfile`, per-second `intensity`). */
  motivationByCode: (code: string) => TermRef | undefined
  dimensionById: (id: ObjectId) => TermRef | undefined
  /** For `SecondData.observational`, which is keyed by dimension code. */
  dimensionByCode: (code: string) => TermRef | undefined
  /** Resolves a pattern or sub-pattern id. */
  patternById: (id: ObjectId) => ResolvedPattern | undefined
  isLoading: boolean
  /** `true` when `/meta` failed. The feature cannot boot without it. */
  isError: boolean
  /**
   * `true` once a payload has arrived. Every other field falls back to an empty
   * value, so this is the only way to tell "loaded and empty" apart from
   * "never loaded" — which is what the provider needs to decide whether a
   * failure is recoverable or fatal to the boot.
   */
  hasMeta: boolean
  refetch: () => void
}

const EMPTY: never[] = []

const indexBy = <T, K extends keyof T>(items: readonly T[], key: K): Map<T[K], T> => new Map(items.map((item) => [item[key], item]))

/**
 * Reads the UA Insights `/meta` payload (networks, months, genres, motivations,
 * dimensions, story patterns) via RTK Query and exposes the defaults and the
 * id/code lookups the rest of the feature reads synchronously.
 */
export const useUAInsightsMeta = (): UAInsightsMeta => {
  const { data, isLoading, isError, refetch } = useGetMetaQuery()

  return useMemo<UAInsightsMeta>(() => {
    const networks = data?.networks ?? EMPTY
    const months = data?.months ?? EMPTY
    const genres = data?.genres ?? EMPTY
    const motivations = data?.motivations ?? EMPTY
    const dimensions = data?.dimensions ?? EMPTY
    const patterns = data?.patterns ?? EMPTY

    const networksById = indexBy(networks, 'id')
    const genresById = indexBy(genres, 'id')
    const motivationsById = indexBy(motivations, 'id')
    const motivationsByCode = indexBy(motivations, 'code')
    const dimensionsById = indexBy(dimensions, 'id')
    const dimensionsByCode = indexBy(dimensions, 'code')

    // Sub-patterns can sit under more than one parent; the first parent in
    // display order wins so breadcrumbs stay deterministic.
    const patternsById = new Map<ObjectId, ResolvedPattern>()
    patterns.forEach((parent) => patternsById.set(parent.id, { pattern: parent, parent: null }))
    patterns.forEach((parent) =>
      parent.subPatterns.forEach((sub) => {
        if (!patternsById.has(sub.id)) patternsById.set(sub.id, { pattern: sub, parent })
      })
    )

    const defaultNetworkId = data?.defaultNetworkId && networksById.has(data.defaultNetworkId) ? data.defaultNetworkId : networks[0]?.id ?? null

    return {
      networks,
      months,
      genres,
      motivations,
      dimensions,
      patterns,
      defaultNetworkId,
      defaultMonth: data?.defaultMonth ?? (months.length ? months[months.length - 1] : null),
      networkById: (id) => networksById.get(id),
      genreById: (id) => genresById.get(id),
      motivationById: (id) => motivationsById.get(id),
      motivationByCode: (code) => motivationsByCode.get(code),
      dimensionById: (id) => dimensionsById.get(id),
      dimensionByCode: (code) => dimensionsByCode.get(code),
      patternById: (id) => patternsById.get(id),
      isLoading,
      isError,
      hasMeta: Boolean(data),
      refetch,
    }
  }, [data, isLoading, isError, refetch])
}
